import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {ValidationStatus} from 'molbio/bulk-assembly/BulkAssemblyValidationConstants';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {GREEN, ORANGE, RED} from 'ui-library/Colors';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {
  BULK_ASSEMBLY_EMPTY_SUCCESS_MESSAGE,
  BULK_ASSEMBLY_ERROR_PREFIX,
  BULK_ASSEMBLY_SUCCESS_PREFIX,
  BULK_ASSEMBLY_WARNING_PREFIX,
} from './BulkAssemblyTableUtil';

interface BulkAssemblyValidationStatusLineProps {
  line: string;
}

function BulkAssemblyValidationStatusLine({line}: BulkAssemblyValidationStatusLineProps): JSX.Element {
  let displayText = line;
  let iconDetails = null;
  if (displayText.startsWith(BULK_ASSEMBLY_ERROR_PREFIX)) {
    iconDetails = VALIDATION_STATUS_TO_ICON_DETAILS[ValidationStatus.ERROR];
    displayText = displayText.slice(BULK_ASSEMBLY_ERROR_PREFIX.length);
  } else if (displayText.startsWith(BULK_ASSEMBLY_WARNING_PREFIX)) {
    iconDetails = VALIDATION_STATUS_TO_ICON_DETAILS[ValidationStatus.WARNING];
    displayText = displayText.slice(BULK_ASSEMBLY_WARNING_PREFIX.length);
  } else if (displayText.startsWith(BULK_ASSEMBLY_SUCCESS_PREFIX)) {
    iconDetails = VALIDATION_STATUS_TO_ICON_DETAILS[ValidationStatus.SUCCESS];
    displayText = displayText.slice(BULK_ASSEMBLY_SUCCESS_PREFIX.length);
  } else if (displayText === BULK_ASSEMBLY_EMPTY_SUCCESS_MESSAGE) {
    iconDetails = VALIDATION_STATUS_TO_ICON_DETAILS[ValidationStatus.SUCCESS];
    displayText = '';
  }
  return (
    <div>
      {iconDetails && (
        <span className={css(styles.icon)}>
          <BenchlingIcon icon={iconDetails.icon} className={css(iconDetails.styleObject)} />
        </span>
      )}
      {displayText}
    </div>
  );
}

interface BulkAssemblyValidationStatusProps {
  typedValue: TypedValue;
}

/**
 * Show bulk assembly validation results in a table cell with one line per message and an icon on each line.
 *
 * Due to limitations with TypedValues, rather than passing down a rich object with validation info, we pass
 * a plain string and parse in this component to decide which icon to show.
 */
export default function BulkAssemblyValidationStatus({
  typedValue,
}: BulkAssemblyValidationStatusProps): JSX.Element {
  const rawText = formatTypedValue(typedValue);
  return (
    <div>
      {rawText.split('\n').map((line, i) => (
        <BulkAssemblyValidationStatusLine key={i} line={line} />
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  icon: {
    marginRight: 4,
  },
  errorIcon: {
    color: RED,
  },
  warningIcon: {
    color: ORANGE,
  },
  successIcon: {
    color: GREEN,
  },
});

export const VALIDATION_STATUS_TO_ICON_DETAILS: Record<
  ValidationStatus,
  {icon: BenchlingIconName; styleObject: object}
> = {
  [ValidationStatus.SUCCESS]: {
    icon: 'request-executed',
    styleObject: styles.successIcon,
  },
  [ValidationStatus.WARNING]: {
    icon: 'status-warning',
    styleObject: styles.warningIcon,
  },
  [ValidationStatus.ERROR]: {
    icon: 'status-failed',
    styleObject: styles.errorIcon,
  },
};
